<template>
  <v-container fluid class="product-container pa-0">
    <!-- Modern Header -->
    <div v-if="showHeader" class="header-section mb-6">
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-package-variant</v-icon>
        </div>
        <div class="flex-grow-1">
          <h1 class="page-title mb-1">Products</h1>
          <p class="page-subtitle mb-0">Manage your products and inventory</p>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0">
        <v-col cols="12" sm="4" md="3" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-plus-circle</v-icon
            >
            <span>Add Product</span>
          </div>
        </v-col>
        <v-col cols="12" sm="4" md="3" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-tag-outline</v-icon
            >
            <span>SKU Management</span>
          </div>
        </v-col>
        <v-col cols="12" sm="4" md="3" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-format-list-bulleted</v-icon
            >
            <span>Product List</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-row class="ma-0 px-3">
      <!-- Add New Product Card -->
      <v-col cols="12" md="4">
        <v-card
          class="modern-card customBorderRadius fill-height"
          elevation="0"
        >
          <div class="card-header-green"></div>
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">mdi-plus-box</v-icon>
            <span class="text-h6 font-weight-medium">Add New Product</span>
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="product.title"
              label="Product Name"
              placeholder="Enter product name"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
              autofocus
              @keyup="uppercase"
            />

            <v-text-field
              v-model="product.sku"
              label="SKU"
              placeholder="Enter SKU"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
            />

            <v-text-field
              v-model="product.price"
              type="number"
              label="Price"
              placeholder="Enter price"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
              prefix="₹"
            />

            <v-textarea
              v-model="product.desc"
              label="Description"
              placeholder="Optional description"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
              rows="3"
            />

            <div class="mb-4">
              <label class="text-caption text--secondary mb-2 d-block"
                >Product Color</label
              >
              <div class="color-picker-wrapper">
                <v-color-picker
                  v-model="product.hex"
                  hide-mode-switch
                  hide-canvas
                  mode="hexa"
                  class="mx-auto"
                  style="max-width: 100%"
                />
              </div>
            </div>

            <v-btn
              block
              color="primary"
              depressed
              style="border-radius: 8px;"
              @click="add"
            >
              <v-icon left size="20">mdi-plus-circle</v-icon>
              Add Product
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Product List -->
      <v-col cols="12" md="8">
        <v-card class="modern-card customBorderRadius" elevation="0">
          <div class="card-header-green"></div>
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2"
              >mdi-format-list-bulleted</v-icon
            >
            <span class="text-h6 font-weight-medium">Product List</span>
          </v-card-title>
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2"
              >mdi-format-list-bulleted</v-icon
            >
            <span class="text-h6 font-weight-medium">Product List</span>
          </v-card-title>
          <v-divider class="mx-4"></v-divider>
          <v-card-text>
            <!-- Loader -->
            <div v-if="preloader" class="text-center py-8">
              <v-progress-circular :width="3" color="primary" indeterminate />
            </div>

            <!-- Product Grid -->
            <v-row v-else-if="productList.length > 0" class="ma-0">
              <v-col
                v-for="product in productList.filter((d) => !d.system)"
                :key="product.id"
                cols="12"
                sm="6"
                class="pa-2"
              >
                <v-card
                  class="product-card customBorderRadius"
                  elevation="0"
                  outlined
                >
                  <v-card-text class="pa-3">
                    <div class="d-flex align-start">
                      <div class="flex-grow-1" style="min-width: 0;">
                        <div class="d-flex align-center mb-1">
                          <v-icon :color="product.hex" size="12" class="mr-2">
                            mdi-circle
                          </v-icon>
                          <h3 class="product-title text-truncate">
                            {{ product.title }}
                          </h3>
                        </div>
                        <div v-if="product.sku" class="product-meta">
                          <v-icon size="14" class="mr-1">mdi-barcode</v-icon>
                          SKU: {{ product.sku }}
                        </div>
                        <div v-if="product.price" class="product-meta">
                          <v-icon size="14" class="mr-1"
                            >mdi-currency-inr</v-icon
                          >
                          {{ product.price }}
                        </div>
                      </div>
                      <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon small v-bind="attrs" v-on="on">
                            <v-icon size="20">mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="productEdit(product)">
                            <v-list-item-icon class="mr-2">
                              <v-icon size="18">mdi-pencil</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>Edit</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="del(product)">
                            <v-list-item-icon class="mr-2">
                              <v-icon size="18" color="error"
                                >mdi-delete</v-icon
                              >
                            </v-list-item-icon>
                            <v-list-item-title>Delete</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Empty State -->
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey lighten-1"
                >mdi-package-variant-closed</v-icon
              >
              <h5 class="text--secondary mt-3">No Products Found</h5>
              <p class="text-caption">Add your first product to get started</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Edit Product Dialog -->
    <v-dialog v-model="dialogProductEdit" max-width="500" scrollable>
      <v-card class="product-dialog">
        <div class="dialog-header-green"></div>
        <v-card-title class="pb-2">
          <v-icon color="primary" class="mr-2">mdi-pencil</v-icon>
          <span class="text-h6">Edit Product</span>
        </v-card-title>
        <v-divider class="mx-4"></v-divider>

        <v-card-text class="pt-4 dialog-content">
          <v-text-field
            v-model="productEditedItem.title"
            label="Product Name"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
            style="border-radius: 8px;"
            :rules="rulesProductname"
            :value="productEditedItem.title.toUpperCase()"
            @input="productEditedItem.title = $event.toUpperCase()"
          />

          <v-textarea
            v-model="productEditedItem.desc"
            label="Description"
            placeholder="Optional description"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
            style="border-radius: 8px;"
            rows="3"
          />

          <v-text-field
            v-model="productEditedItem.sku"
            label="SKU"
            placeholder="Enter SKU"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
            style="border-radius: 8px;"
          />

          <v-text-field
            v-model="productEditedItem.price"
            label="Price"
            placeholder="Enter price"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
            style="border-radius: 8px;"
            prefix="₹"
          />

          <div class="mb-2">
            <label class="text-caption text--secondary mb-2 d-block"
              >Product Color</label
            >
            <div class="color-picker-wrapper">
              <v-color-picker
                v-model="productEditedItem.hex"
                hide-mode-switch
                mode="hexa"
                class="mx-auto"
              />
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            style="border-radius: 8px;"
            @click="dialogProductEdit = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            depressed
            style="border-radius: 8px;"
            @click="updateProductName"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
const _ = require("lodash");
import fb from "../js/firebase";
import validator from "../js/validator.js";

export default {
  components: {},
  data() {
    return {
      showHeader: false,
      dialogProductEdit: false,
      productEditedItem: {
        title: "",
        desc: "",
        hex: "",
        sku: "",
        price: "",
      },
      preloader: false,
      product: {
        title: "",
        desc: "",
        hex: "",
        sku: "",
        price: "",
      },
      rulesProductname: [
        (v) =>
          /^\w+$/.test(v) ||
          "Maximum Fifteen characters & Must Not Contain Special Characters Or Space",
      ],
      productList: [],
    };
  },
  computed: {
    // productList(v) {
    //   return this.$store.getters.productList;
    // },
  },
  watch: {
    // productList() {
    //   this.preloader = false;
    // },
  },
  created() {
    this.showHeader = localStorage.getItem("productHeaderDismissed") !== "true";
    this.getProducts();
  },

  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("productHeaderDismissed", "true");
    },
    uppercase() {
      this.product.title = this.product.title.toUpperCase();
    },
    productEdit(item) {
      this.productEditedItem = item;
      this.dialogProductEdit = true;
    },
    updateProductName() {
      const self = this;

      if (this.productEditedItem.title == "") {
        return self.$swal({
          type: "error",
          text: "Please Enter Product Name",
        });
      }

      self.$http
        .patch(
          `${this.$store.state.ENDPOINT}/products/${self.productEditedItem.id}`,
          self.productEditedItem,
        )
        .then(
          (response) => {
            self.dialogProductEdit = false;
            self.getProducts();
            self.$swal({
              type: "success",
              text: "Product Updated Successfully",
            });
          },
          (error) => {
            self.$swal({
              type: "warning",
              text: "Error occured. Please try again.",
            });
          },
        );
    },

    async getProducts() {
      var self = this;
      var filter = {
        where: {
          companyId: this.$store.getters.user.companyId,
        },
      };

      try {
        var response = await this.$http.get(
          `${self.$store.state.ENDPOINT}/products?filter=${encodeURIComponent(
            JSON.stringify(filter),
          )}`,
        );
        self.productList = response.body;

        self.loader = false;
      } catch (err) {
        console.log("Error", err);

        self.loader = false;

        self.$swal({
          type: "error",
          text: "Something went wrong. Please try again",
        });
      }
    },

    add() {
      if (this.product.title == "") {
        return self.$swal({
          type: "error",
          text: "Please Enter Product Name",
        });
      }

      this.product.title = this.product.title.toUpperCase().trim();
      this.preloader = true;
      this.product.companyId = this.$store.getters.user.companyId;

      this.$http
        .post(`${this.$store.state.ENDPOINT}/products`, this.product)
        .then((response) => {
          this.product = {};
          //   this.product.title = "";
          this.product.hex = "";
          this.product.desc = "";
          this.product.sku = "";
          this.product.price = "";
          this.preloader = false;
          this.getProducts();
          fb.log("create_products");
        })
        .catch((e) => {
          this.preloader = false;
          this.$swal({
            type: "error",
            text: "Sorry, same product is already present",
          });
        });
    },
    del(item) {
      this.preloader = true;

      this.$swal
        .fire({
          title: "Are you sure?",
          text: "Delete this product?",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            this.$http
              .delete(`${this.$store.state.ENDPOINT}/products/${item.id}`)
              .then((response) => {
                this.preloader = false;
                this.getProducts();
                // this.$store.dispatch("getProducts", {
                //   companyId: this.$store.getters.user.companyId,
                // });
                fb.log("delete_product");
              })
              .catch((e) => {
                this.preloader = false;
                console.log(e);
              });
          }
          this.preloader = false;
        });
    },
  },
};
</script>

<style scoped>
.product-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .product-container {
  background-color: #121212 !important;
}

.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  padding: 24px;
  color: white;
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.2);
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: white;
  margin: 0;
}

.page-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.feature-pills {
  margin-top: 16px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: white;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.modern-card {
  background: white;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.modern-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.customBorderRadius {
  border-radius: 12px !important;
}

.product-dialog {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.dialog-content {
  max-height: 60vh;
  overflow-y: auto;
}

/* Custom scrollbar for dialog */
.dialog-content::-webkit-scrollbar {
  width: 8px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 0 12px 12px 0;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #37cf37;
  border-radius: 4px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #2ba82b;
}

/* Rounded inputs in dialog */
.product-dialog .v-text-field--outlined fieldset,
.product-dialog .v-textarea--outlined fieldset {
  border-radius: 8px !important;
}

.card-header-green {
  height: 4px;
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px 12px 0 0;
}

.dialog-header-green {
  height: 4px;
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px 12px 0 0;
}

.color-picker-wrapper {
  overflow: hidden;
  border-radius: 8px;
}

.color-picker-wrapper .v-color-picker {
  border-radius: 8px !important;
}

.product-card {
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.product-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.product-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.product-meta {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
  display: flex;
  align-items: center;
}
</style>
